import { createRoute, useRouter, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { CheckCircle2, Loader2, XCircle } from 'lucide-react'
import rootRoute from './rootRoute'
import { Button, Card, toast } from '@/components/ui'
import { $client } from '@/lib/fetch'

const VerifyEmailPage = () => {
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>(
    'verifying',
  )
  const search = useSearch({ from: '/verify-email' })
  const router = useRouter()

  useEffect(() => {
    const verify = async () => {
      if (!search.email || !search.token) {
        setStatus('error')
        return
      }
      const resp = await $client.POST('/api/auth/verify-email', {
        body: { email: search.email, token: search.token },
      })
      if (resp.error != null) {
        setStatus('error')
      } else {
        setStatus('success')
        toast({ color: 'success', title: 'Email vérifié avec succès !' })
        setTimeout(
          () => router.navigate({ to: '/login', search: { go: undefined } }),
          3000,
        )
      }
    }
    verify()
  }, [search.email, search.token, router])

  return (
    <div
      style={{
        maxWidth: 520,
        margin: '0 auto',
        padding: 'var(--space-20) var(--gutter)',
      }}
    >
      <Card padding={40} style={{ textAlign: 'center' }}>
        <div className="oumma-overline" style={{ marginBottom: 12 }}>
          Vérification de l'email
        </div>
        {status === 'verifying' && (
          <>
            <Loader2
              size={48}
              style={{
                color: 'var(--emerald-700)',
                margin: '12px auto',
                animation: 'spin 1s linear infinite',
              }}
            />
            <p style={{ color: 'var(--ink-2)' }}>
              Vérification de votre email en cours…
            </p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2
              size={56}
              style={{ color: 'var(--success)', margin: '12px auto' }}
            />
            <h2 style={{ fontSize: 'var(--text-h3)', margin: '0 0 8px' }}>
              Email vérifié !
            </h2>
            <p style={{ color: 'var(--ink-2)', marginBottom: 22 }}>
              Votre compte est activé. Redirection vers la connexion…
            </p>
            <Button
              variant="primary"
              onClick={() =>
                router.navigate({ to: '/login', search: { go: undefined } })
              }
            >
              Se connecter
            </Button>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle
              size={56}
              style={{ color: 'var(--danger)', margin: '12px auto' }}
            />
            <h2 style={{ fontSize: 'var(--text-h3)', margin: '0 0 8px' }}>
              Échec de la vérification
            </h2>
            <p style={{ color: 'var(--ink-2)', marginBottom: 22 }}>
              Le lien de vérification est invalide ou a expiré.
            </p>
            <Button
              variant="secondary"
              onClick={() => router.navigate({ to: '/signup' })}
            >
              Créer un nouveau compte
            </Button>
          </>
        )}
      </Card>
    </div>
  )
}

export const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email',
  validateSearch: (search) => ({
    email: search.email as string | undefined,
    token: search.token as string | undefined,
  }),
}).lazy(() => import('./VerifyEmailPage.lazy').then((m) => m.Route))

export default VerifyEmailPage
